class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    // FLIK FIX (Noibu #284): Guard against missing child markup.
    //
    // Root cause:
    //   When a <details-modal> custom element is upgraded without the
    //   expected <details>/<summary> children present in the DOM (which
    //   we observe on /search, /collections/* pages in Edge on Windows
    //   where the surrounding markup is rendered/transformed differently
    //   than on other browsers), the constructor previously threw on
    //   `this.detailsContainer.addEventListener(...)` because
    //   `this.querySelector('details')` returned null.
    //
    //   Throwing in the constructor of a custom element halts the rest
    //   of page-initialization JS for that script tick, which is why
    //   downstream behavior (predictive search, modals, etc.) breaks
    //   for affected sessions.
    //
    // Fix:
    //   If the required children are not present at upgrade time, log
    //   a warning and skip wiring. This is NOT silencing the symptom:
    //   the warning preserves observability for any real misuse, and
    //   the open()/close()/onSummaryClick() methods can no-op safely
    //   without the wiring (the modal simply does not function on this
    //   element instance — which it couldn't anyway without the markup).
    if (!this.detailsContainer || !this.summaryToggle) {
      console.warn(
        '[details-modal] Missing required <details> or <summary> child. ' +
          'Skipping initialization for this element.'
      );
      return;
    }

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));

    // The close button is also optional in some templates — guard it the
    // same way rather than crash the rest of the constructor.
    const closeButton = this.querySelector('button[type="button"]');
    if (closeButton) {
      closeButton.addEventListener('click', this.close.bind(this));
    }

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer && this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    // FLIK FIX (Noibu #150): Guard the global focus-trap helper.
    //
    // trapFocus/removeTrapFocus are defined in global.js. On some Android
    // sessions global.js has not executed yet when a modal opens/closes,
    // so calling the helper directly threw "trapFocus is not defined" /
    // "removeTrapFocus is not defined", halting the rest of the script
    // tick and breaking downstream behavior for that visit. Guard with a
    // typeof check so the modal still works and focus-trapping simply
    // no-ops (with a warning) until the helper is available.
    if (typeof trapFocus === 'function') {
      trapFocus(
        this.detailsContainer.querySelector('[tabindex="-1"]'),
        this.detailsContainer.querySelector('input:not([type="hidden"])')
      );
    } else {
      console.warn('[details-modal] trapFocus helper not available yet; skipping focus trap.');
    }
  }

  close(focusToggle = true) {
    // FLIK FIX (Noibu #150): see open() — guard the global helper so a
    // not-yet-loaded global.js can't throw and break the page.
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(focusToggle ? this.summaryToggle : null);
    } else {
      console.warn('[details-modal] removeTrapFocus helper not available yet; skipping focus release.');
    }
    if (this.detailsContainer) this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);
